/* === Ask Angie — 4-Agent Multi-Agent System ===
   Each user query flows through 4 named, sequential agents.
   The stage indicator in the UI shows which agent is running.

   Agent 1: Teacher       — parses query, identifies goal (items, sizes, colors, qty)
   Agent 2: Retrieval     — pulls broad candidate products from Shopify
   Agent 3: Verification  — scores candidates against goal, picks best variant per item
   Agent 4: Presentation  — formats verified picks for the dealer-facing row UI

   Each agent returns a plain object that's fed into the next.
   The orchestrator (askAngieRun) coordinates the pipeline and emits stage events.
*/

(function (root) {
  'use strict';

  /* =========================================================
     SHARED REFERENCE DATA — dealer terminology + shortcuts
     ========================================================= */

  var SYNONYMS = [
    { test: /\b(5\.5|5\s*1\/2|5-1\/2)\s*(in|inch|inches)?\s*(numbers?)?\b/i, replacement: '5 1/2 windshield numbers' },
    { test: /\b(7\.5|7\s*1\/2|7-1\/2)\s*(in|inch|inches)?\s*(numbers?)?\b/i, replacement: '7 1/2 windshield numbers' },
    { test: /\b(9\.5|9\s*1\/2|9-1\/2)\s*(in|inch|inches)?\s*(numbers?)?\b/i, replacement: '9 1/2 windshield numbers' },
    { test: /\b(11\.5|11\s*1\/2|11-1\/2)\s*(in|inch|inches)?\s*(numbers?)?\b/i, replacement: '11 1/2 windshield numbers' },
    { test: /\b5'?s\b/i,  replacement: '5 1/2 windshield numbers' },
    { test: /\b7'?s\b/i,  replacement: '7 1/2 windshield numbers' },
    { test: /\b9'?s\b/i,  replacement: '9 1/2 windshield numbers' },
    { test: /\b11'?s\b/i, replacement: '11 1/2 windshield numbers' },
    { test: /\b(chartreuse|day[- ]glow green|fluor(escent)? green|hi[- ]?vis green)\b/i, replacement: 'neon green' },
    { test: /\b(poly[- ]?tags?|polytags?|poly key)\b/i, replacement: 'key tags poly' },
    { test: /\bkey ?tags?\b/i, replacement: 'key tags' },
    { test: /\bautomatic\b/i, replacement: 'automatic windshield slogan' },
    { test: /\bone owner\b/i, replacement: 'one owner windshield slogan' },
    { test: /\b(low mileage|low miles)\b/i, replacement: 'low miles windshield slogan' },
    { test: /\bslogans?\b/i, replacement: 'windshield slogan stickers' },
    { test: /\bstar(s)?\b/i, replacement: 'windshield decorative stars' },
    { test: /\bhang ?tag(s)?\b/i, replacement: 'rearview mirror hang tag' },
    { test: /\boval(s)?\b/i, replacement: 'windshield oval signs' },
    { test: /\b(buyer'?s? guide|ftc form)\b/i, replacement: 'buyers guide' },
    { test: /\b(deal jacket|deal folder)\b/i, replacement: 'deal jacket' },
    { test: /\b(balloon|balon|ballon)(s|es)?\b/i, replacement: 'reusable balloon' },
    { test: /\b(feather ?flag)s?\b/i, replacement: 'feather flag' },
    { test: /\b(bow|ribbon)s?\b/i, replacement: 'premium bow' },
    { test: /\bwindshield marker(s)?\b/i, replacement: 'wide tip windshield marker' }
  ];

  var SHORTCUT_HANDLES = [
    { test: /\b(9\.5|9\s*1\/2|9-1\/2|9'?s)\b/i,                                   handle: 'windshield-number-stickers-9-5' },
    { test: /\b(7\.5|7\s*1\/2|7-1\/2|7'?s)\b/i,                                   handle: 'windshield-number-stickers-7-5' },
    { test: /\b(5\.5|5\s*1\/2|5-1\/2|5'?s)\b/i,                                   handle: 'windshield-number-stickers-5-5' },
    { test: /\b(11\.5|11\s*1\/2|11-1\/2|11'?s|super[- ]?size)\b/i,                handle: 'windshield-number-stickers-11-5' },
    { test: /\b(slogan|automatic|one[- ]?owner|low[- ]?miles|as[- ]?is|wow|sale sticker|hot buy|loaded|reduced|new arrival|must see)\b/i, handle: 'windshield-slogan-stickers' },
    { test: /\b(key ?tags?|polytags?|poly[- ]?tags?|poly[- ]?key)\b/i,            handle: 'key-tags' },
    { test: /\b(hang ?tags?)\b/i,                                                 handle: 'windshield-hang-tags' },
    { test: /\b(stars?|decorative[- ]?sticker)\b/i,                               handle: 'windshield-decorative-stickers' },
    { test: /\boval(s)?\b/i,                                                      handle: 'windshield-oval-signs' },
    { test: /\b(feather ?flag)s?\b/i,                                             handle: 'stock-feather-flags' },
    { test: /\b(balloon|balon)(s|es)?\b/i,                                        handle: 'non-helium-reusable-balloon' }
  ];

  var WORD_NUMS = { one:1,two:2,three:3,four:4,five:5,six:6,seven:7,eight:8,nine:9,ten:10,eleven:11,twelve:12,fifteen:15,twenty:20,thirty:30,forty:40,fifty:50,hundred:100,dozen:12 };

  function applySynonyms(q) {
    var r = q;
    for (var i = 0; i < SYNONYMS.length; i++) {
      if (SYNONYMS[i].test.test(r)) r = r.replace(SYNONYMS[i].test, SYNONYMS[i].replacement);
    }
    return r.replace(/\s+/g, ' ').trim();
  }

  function shortcutHandle(q) {
    for (var i = 0; i < SHORTCUT_HANDLES.length; i++) {
      if (SHORTCUT_HANDLES[i].test.test(q)) return SHORTCUT_HANDLES[i].handle;
    }
    return null;
  }

  /* =========================================================
     SHARED HTTP HELPERS
     ========================================================= */

  var productCache = {};
  function fetchProduct(handle) {
    if (productCache[handle]) return Promise.resolve(productCache[handle]);
    return fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) { if (d) productCache[handle] = d; return d; })
      .catch(function () { return null; });
  }

  function predictiveSearch(q, limit) {
    if (!q) return Promise.resolve([]);
    var url = '/search/suggest.json?q=' + encodeURIComponent(q) +
              '&resources[type]=product&resources[limit]=' + (limit || 5) +
              '&resources[options][unavailable_products]=last';
    return fetch(url, { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) {
        return (d && d.resources && d.resources.results && d.resources.results.products) || [];
      })
      .catch(function () { return []; });
  }

  function parsePrice(p) {
    if (typeof p === 'number') return p < 100 ? p : p / 100;
    if (!p) return 0;
    return parseFloat(String(p).replace(/[^0-9.]/g, '')) || 0;
  }

  /* =========================================================
     AGENT 1 — TEACHER
     Goal: read the dealer's request and produce a clear structured goal
     Input:  raw query string ("3 packs of 9s neon green, 1 polytag")
     Output: { items: [{rawText, qty, qWords, expanded, hints}], summary }
     ========================================================= */

  function agentTeacher(rawQuery) {
    var clean = (rawQuery || '').trim();
    if (!clean) return { items: [], summary: 'empty query' };

    // Split into items by common separators
    var parts = clean.split(/[,;]|\s+and\s+|\s+plus\s+|\s+&\s+/i)
      .map(function (s) { return s.trim(); })
      .filter(Boolean);

    var items = parts.map(function (line) {
      return parseItemLine(line);
    });

    var summary = items.length === 1
      ? 'Looking for: ' + items[0].itemLabel
      : 'Looking for ' + items.length + ' items';

    return { items: items, summary: summary, raw: clean };
  }

  function parseItemLine(line) {
    var qty = 1, multiplier = 1, remainder = line;
    var m = line.match(/^(\d+)\s*(dozen|doz\.?|packs?|boxes?|cases?|rolls?)?\s+(?:of\s+)?(.+)/i);
    if (m) {
      qty = parseInt(m[1], 10) || 1;
      var unit = (m[2] || '').toLowerCase();
      if (unit.indexOf('dozen') === 0 || unit.indexOf('doz') === 0) multiplier = 12;
      remainder = m[3];
    } else {
      var w = line.match(/^(a|an|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|fifteen|twenty|thirty|forty|fifty)\s+(dozen|doz\.?|packs?|boxes?)?\s+(?:of\s+)?(.+)/i);
      if (w) {
        var k = w[1].toLowerCase();
        qty = (k === 'a' || k === 'an') ? 1 : (WORD_NUMS[k] || 1);
        var u = (w[2] || '').toLowerCase();
        if (u.indexOf('dozen') === 0 || u.indexOf('doz') === 0) multiplier = 12;
        remainder = w[3];
      }
    }

    var expanded = applySynonyms(remainder);
    var hint = shortcutHandle(remainder);

    return {
      original: line,
      qty: qty * multiplier,
      itemLabel: remainder.trim(),
      qWords: remainder.toLowerCase().split(/\s+/).filter(function (w) { return w.length > 1; }),
      expanded: expanded,
      shortcutHandle: hint
    };
  }

  /* =========================================================
     AGENT 2 — RETRIEVAL
     Goal: find every plausible product matching each goal item
     Input:  goal from Teacher
     Output: { items: [{...goalItem, candidates: [productSummary, ...]}] }
     ========================================================= */

  function agentRetrieval(goal) {
    var promises = goal.items.map(function (item) {
      return retrieveCandidates(item).then(function (candidates) {
        return Object.assign({}, item, { candidates: candidates });
      });
    });
    return Promise.all(promises).then(function (items) {
      return { items: items, summary: 'Searching ' + items.length + ' items in the catalog' };
    });
  }

  function retrieveCandidates(item) {
    // Strategy: shortcut handle (gold), then synonym-expanded suggest, then last-N-word suggest, then raw suggest
    var jobs = [];
    if (item.shortcutHandle) {
      jobs.push(fetchProduct(item.shortcutHandle).then(function (p) { return p ? [{ source: 'shortcut', product: p }] : []; }));
    }
    jobs.push(predictiveSearch(item.expanded, 6).then(function (ps) {
      return ps.map(function (p) { return { source: 'suggest-expanded', product: p }; });
    }));
    if (item.qWords.length > 2) {
      jobs.push(predictiveSearch(item.qWords.slice(-3).join(' '), 4).then(function (ps) {
        return ps.map(function (p) { return { source: 'suggest-tail', product: p }; });
      }));
    }
    jobs.push(predictiveSearch(item.itemLabel, 4).then(function (ps) {
      return ps.map(function (p) { return { source: 'suggest-raw', product: p }; });
    }));

    return Promise.all(jobs).then(function (results) {
      var seen = {};
      var merged = [];
      results.forEach(function (arr) {
        arr.forEach(function (c) {
          var h = c.product && c.product.handle;
          if (h && !seen[h]) { seen[h] = true; merged.push(c); }
        });
      });
      return merged;
    });
  }

  /* =========================================================
     AGENT 3 — VERIFICATION
     Goal: score each candidate against the goal; pick best variant; reject zero-price/no-variant ghosts
     Input:  retrieval output
     Output: { items: [{...goalItem, match: {product, variant, price, score} | null, suggestions: [...]}] }
     ========================================================= */

  function agentVerification(retrieved) {
    var promises = retrieved.items.map(function (item) {
      return verifyItem(item);
    });
    return Promise.all(promises).then(function (items) {
      var matched = items.filter(function (it) { return it.match; }).length;
      return { items: items, summary: 'Verified ' + matched + ' of ' + items.length + ' items' };
    });
  }

  function verifyItem(item) {
    // Score each candidate by keyword overlap on product title + variant titles
    var scored = item.candidates.map(function (c) {
      var t = ((c.product.title || '') + '').toLowerCase();
      var score = 0;
      item.qWords.forEach(function (w) { if (t.indexOf(w) !== -1) score += 1; });
      if (c.product.available !== false) score += 0.5;
      if (c.source === 'shortcut') score += 3;
      if (c.source === 'suggest-expanded') score += 0.5;
      return { c: c, score: score };
    });
    scored.sort(function (a, b) { return b.score - a.score; });
    if (!scored.length) return Promise.resolve(Object.assign({}, item, { match: null, suggestions: [] }));

    var topHandle = scored[0].c.product.handle;
    var suggestions = scored.slice(1, 4).map(function (s) {
      return {
        handle: s.c.product.handle,
        title: s.c.product.title,
        price: parsePrice(s.c.product.price),
        image: s.c.product.featured_image || s.c.product.image
      };
    });

    return fetchProduct(topHandle).then(function (full) {
      if (!full) return Object.assign({}, item, { match: null, suggestions: suggestions });
      var variant = pickVariant(full, item);
      if (!variant) return Object.assign({}, item, { match: null, suggestions: suggestions });
      var price = parsePrice(variant.price);
      if (!price) return Object.assign({}, item, { match: null, suggestions: suggestions });
      return Object.assign({}, item, {
        match: {
          product: full,
          variant: variant,
          price: price,
          score: scored[0].score,
          confidence: scored[0].score > 2 ? 'high' : 'medium'
        },
        suggestions: suggestions
      });
    });
  }

  function pickVariant(product, item) {
    var variants = product.variants || [];
    if (!variants.length) return null;
    if (variants.length === 1) return variants[0];
    var scored = variants.map(function (v) {
      var vt = ((v.title || '') + ' ' + (v.option1 || '') + ' ' + (v.option2 || '') + ' ' + (v.option3 || '')).toLowerCase();
      var s = 0;
      item.qWords.forEach(function (w) { if (vt.indexOf(w) !== -1) s += 1; });
      if (v.available) s += 0.5;
      return { v: v, s: s };
    });
    scored.sort(function (a, b) { return b.s - a.s; });
    return scored[0].v;
  }

  /* =========================================================
     AGENT 4 — PRESENTATION
     Goal: shape verified data into the row-friendly objects the UI renders
     Input:  verification output
     Output: { rows: [{id, title, productTitle, variantTitle, price, qty, ...}], summary }
     ========================================================= */

  function agentPresentation(verified) {
    var rows = verified.items.map(function (item, i) {
      if (item.match) {
        var p = item.match.product, v = item.match.variant;
        var label = p.title + (v.title && v.title !== 'Default Title' ? ' — ' + v.title : '');
        return {
          id: 'r' + Date.now() + '_' + i,
          qty: item.qty || 1,
          query: item.itemLabel,
          original: item.original,
          title: label,
          productTitle: p.title,
          variantTitle: v.title || '',
          price: item.match.price,
          productId: p.id,
          variantId: v.id,
          handle: p.handle,
          image: p.featured_image || (p.images && p.images[0]) || '',
          available: !!v.available,
          confidence: item.match.confidence,
          matched: true,
          options: p.options || [],
          variants: p.variants || [],
          suggestions: item.suggestions || []
        };
      }
      return {
        id: 'r' + Date.now() + '_' + i,
        qty: item.qty || 1,
        query: item.itemLabel,
        original: item.original,
        title: item.itemLabel,
        price: 0,
        matched: false,
        suggestions: item.suggestions || []
      };
    });
    return { rows: rows, summary: 'Showing ' + rows.length + ' results' };
  }

  /* =========================================================
     ORCHESTRATOR
     Walks the 4 agents in order, emitting onStage events so the UI can
     show "Angie is thinking — Step 2: searching the catalog..."
     ========================================================= */

  function askAngieRun(rawQuery, onStage) {
    onStage = onStage || function () {};
    onStage('teacher', { label: 'Understanding your request', summary: '' });
    var goal = agentTeacher(rawQuery);
    onStage('teacher', { label: 'Understood your request', summary: goal.summary, done: true });

    onStage('retrieval', { label: 'Searching the catalog', summary: '' });
    return agentRetrieval(goal).then(function (retrieved) {
      onStage('retrieval', { label: 'Found candidates', summary: retrieved.summary, done: true });

      onStage('verification', { label: 'Verifying matches', summary: '' });
      return agentVerification(retrieved);
    }).then(function (verified) {
      onStage('verification', { label: 'Verified matches', summary: verified.summary, done: true });

      onStage('presentation', { label: 'Preparing your order', summary: '' });
      var presented = agentPresentation(verified);
      onStage('presentation', { label: 'Ready', summary: presented.summary, done: true });
      return presented;
    });
  }

  // Expose
  root.AskAngieMAS = {
    run: askAngieRun,
    agentTeacher: agentTeacher,
    agentRetrieval: agentRetrieval,
    agentVerification: agentVerification,
    agentPresentation: agentPresentation,
    fetchProduct: fetchProduct,
    pickVariant: pickVariant,
    parsePrice: parsePrice,
    applySynonyms: applySynonyms,
    shortcutHandle: shortcutHandle
  };
})(window);
